import axios, { AxiosInstance, AxiosResponse, AxiosRequestConfig } from 'axios'
import { UserTypeService } from '../UserTypeService'
import { IHttpClient } from './IHttpClient'
import { AuthService } from '../auth'
import { getAuthToken } from '@/utils/cookieUtils'
import { RetryHelper } from '@/utils/RetryHelper'

/**
 * Service identifiers for BFF routing
 */
export enum AttaServiceType {
	ATTA_SERVICES = 'api-service',
	CREDITO_IMOBILIARIO = 'api-credit',
	GUARDIAN = 'guardian',
	WORKFLOW = 'api-workflow'
}

/**
 * Mapping of base URLs to BFF service identifiers
 */
export const BFF_SERVICE_MAPPING: Record<string, AttaServiceType> = {
	[`${import.meta.env.VUE_APP_API_ATTA_SERVICES}`]: AttaServiceType.ATTA_SERVICES,
	[`${import.meta.env.VUE_APP_API_CREDITO_IMOBILIARIO}`]: AttaServiceType.CREDITO_IMOBILIARIO,
	[`${import.meta.env.VUE_APP_API_GUARDIAN}`]: AttaServiceType.GUARDIAN,
	[`${import.meta.env.VUE_APP_API_WORKFLOW}`]: AttaServiceType.WORKFLOW
}

/**
 * Custom params serializer that removes brackets from array parameters
 */
const customParamsSerializer = (params: any) => {
	const searchParams = new URLSearchParams()

	Object.entries(params).forEach(([key, value]) => {
		if (value === null || value === undefined) {
			return
		}

		if (Array.isArray(value)) {
			value.forEach((item) => {
				if (item !== null && item !== undefined) {
					searchParams.append(key, String(item))
				}
			})
		} else {
			searchParams.append(key, String(value))
		}
	})

	return searchParams.toString()
}

/**
 * BFF HTTP Client - Routes requests through BFF with URI transformation
 * Uses lazy initialization to ensure UserTypeService is ready before determining BFF URL
 */
export class BffHttpClient implements IHttpClient {
	private serviceType: AttaServiceType
	private headers?: any
	private httpClient: AxiosInstance | null = null

	constructor(attaBaseUrl: string, headers?: any) {
		this.serviceType = BFF_SERVICE_MAPPING[attaBaseUrl]
		if (!this.serviceType) {
			throw new Error(`Unknown service for base URL: ${attaBaseUrl}`)
		}

		this.headers = headers
	}

	private getHttpClient(): AxiosInstance {
		if (!this.httpClient) {
			const baseUrl = this.getBffBaseUrl()
			this.httpClient = this.createHttpClient(baseUrl, this.headers)
		}
		return this.httpClient
	}

	private createHttpClient(baseUrl: string | undefined, headers: any = undefined): AxiosInstance {
		var http = axios.create({
			baseURL: baseUrl,
			withCredentials: true, // Enable cookies for cross-origin requests
			paramsSerializer: customParamsSerializer
		})

		http.defaults.headers.post['Content-Type'] = 'application/json'

		if (UserTypeService.isExternal()) {
			http.interceptors.request.use(
				(config) => {
					const authToken = getAuthToken()
					if (config.headers && !config.headers['Authorization'] && authToken) {
						config.headers['Authorization'] = `Bearer ${authToken}`
					}

					return config
				},
				(error) => Promise.reject(error)
			)
		}

		http.interceptors.response.use(
			(response) => {
				sessionStorage.removeItem('LOGIN_REDIRECTED')
				return response
			},
			(error) => {
				if (error.response && error.response.status === 401 && sessionStorage.getItem('LOGIN_REDIRECTED') != String(true)) {
					sessionStorage.setItem('LOGIN_REDIRECTED', String(true))
					AuthService.login()
				}
				return Promise.reject(error)
			}
		)

		http.interceptors.response.use((response) => response, RetryHelper.createRetryInterceptor(http))

		if (headers) {
			Object.assign(http.defaults.headers, headers)
		}

		return http
	}

	private getBffBaseUrl(): string {
		const userType = UserTypeService.getUserType()

		if (userType === 'internal') {
			return import.meta.env.VUE_APP_API_BACKOFFICE_BFF
		} else {
			return import.meta.env.VUE_APP_API_PORTFOLIO_MANAGER
		}
	}

	private transformUri(originalPath: string): string {
		return `/v1/isolve/${this.serviceType}${originalPath}`
	}

	get<T = any, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
		const transformedUrl = this.transformUri(url)
		return this.getHttpClient().get<T, R>(transformedUrl, config)
	}

	post<T = any, R = AxiosResponse<T>>(url: string, data?: any, config?: AxiosRequestConfig): Promise<R> {
		const transformedUrl = this.transformUri(url)
		return this.getHttpClient().post<T, R>(transformedUrl, data, config)
	}

	put<T = any, R = AxiosResponse<T>>(url: string, data?: any, config?: AxiosRequestConfig): Promise<R> {
		const transformedUrl = this.transformUri(url)
		return this.getHttpClient().put<T, R>(transformedUrl, data, config)
	}

	delete<T = any, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
		const transformedUrl = this.transformUri(url)
		return this.getHttpClient().delete<T, R>(transformedUrl, config)
	}

	patch<T = any, R = AxiosResponse<T>>(url: string, data?: any, config?: AxiosRequestConfig): Promise<R> {
		const transformedUrl = this.transformUri(url)
		return this.getHttpClient().patch<T, R>(transformedUrl, data, config)
	}

	get defaults() {
		return this.getHttpClient().defaults
	}

	get interceptors() {
		return this.getHttpClient().interceptors
	}
}
